"use client";

import { Check, ChevronsUpDown, ListChecks, X } from "lucide-react";
import { Fragment, useEffect, useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Button } from "../ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "../ui/command";
import { cn } from "~/../lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { useTranslation } from "react-i18next";
import { distinctStrings } from "~/utils/array-util";
import { throttle } from "lodash-es";
import type { FormOption } from "~/types/ui";

type Primitive = string | number | boolean;

function ComboBoxItem<T extends Primitive>({
  opt,
  selected,
  onSelect,
}: {
  opt: FormOption<T>;
  selected: T[];
  onSelect: () => void;
}) {
  return (
    <CommandItem onSelect={onSelect} onMouseDown={(e) => e.preventDefault()}>
      <div
        className={
          opt.itemAction !== undefined
            ? "flex align-center justify-between w-full"
            : ""
        }
      >
        <div
          className={
            opt.description ? "flex flex-col justify-start align-start" : ""
          }
        >
          <div className="flex justify-start align-start w-full">
            {selected.includes(opt.value) && (
              <Check
                className={cn(
                  "mr-2 h-4 w-4",
                  selected.includes(opt.value) ? "opacity-100" : "opacity-0",
                )}
              />
            )}
            {opt.label}
          </div>
          {opt.description && (
            <span className="w-full text-xs opacity-50">{opt.description}</span>
          )}
        </div>
        {opt.itemAction}
      </div>
    </CommandItem>
  );
}

interface ComboBoxProps<T extends Primitive = Primitive> {
  options: FormOption<T>[];
  placeholder?: string;
  value?: T | T[] | undefined;
  onChange?: (value: T | T[]) => void;
  isMulti?: boolean;
  disabled?: boolean;
  clearable?: boolean;
  searchQuery?: string;
  onSearchChange?: (search: string) => void;
  externalFilter?: boolean;
  className?: string;
  contentClassName?: string;
  /** Called when the dropdown opens or closes, e.g. to commit a multi-select only once
   * the user is done picking. */
  onOpenChange?: (open: boolean) => void;
}

export function ComboBox<T extends Primitive = Primitive>({
  options,
  placeholder = "Select...",
  value,
  onChange,
  searchQuery,
  onSearchChange = () => {},
  isMulti = false,
  clearable = false,
  externalFilter = false,
  disabled,
  className = "",
  contentClassName = "",
  onOpenChange,
}: ComboBoxProps<T>) {
  const { t } = useTranslation();
  const [open, setOpenState] = useState(false);
  const setOpen = (next: boolean) => {
    setOpenState(next);
    onOpenChange?.(next);
  };
  const [search, setSearch] = useState(searchQuery || "");
  const [categories, setCategories] = useState<string[]>([]);

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const lowerSerach = search.toLowerCase();

    return options.filter(
      (
        opt, // Check if any of the search keys match the label or value
      ) => {
        // External filtering
        if (externalFilter) return true;

        return (
          opt.label.toLowerCase().includes(lowerSerach) ||
          opt.value.toString().toLowerCase().includes(lowerSerach)
        );
      },
    );
  }, [search, options]);

  const selected: T[] = useMemo(() => {
    if (value === undefined) return [];
    if (Array.isArray(value)) return value;
    if (value !== undefined && value !== null) return [value];
    return [];
  }, [value]);

  const toggleValue = (v: T) => {
    let newValue: T[];

    if (isMulti) {
      const exists = selected.includes(v);
      newValue = exists
        ? selected.filter((item) => item !== v)
        : [...selected, v];
      onChange?.(newValue);
    } else {
      onChange?.(v);
      setOpen(false);
    }
  };

  const selectedLabels = useMemo(() => {
    return options
      .filter((opt) => selected.includes(opt.value))
      .map((opt) => opt.label)
      .join(", ");
  }, [selected, options]);

  useEffect(() => {
    const cats = distinctStrings(options.map((opt) => opt.category));
    if (cats.length === 1 && cats[0] === undefined) setCategories([]);
    else setCategories(cats);
  }, [options]);

  function handleSearchChange(val: string) {
    onSearchChange(val);
  }

  const throttleSearchChange = useMemo(
    () =>
      throttle(handleSearchChange, 1500, {
        trailing: true,
        leading: false,
      }),
    [],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild className={className}>
        <Button
          variant="outline"
          role="combobox"
          disabled={disabled}
          className={"w-full justify-between"}
          style={{ zIndex: 10 }}
        >
          <span className="overflow-hidden text-overflow-ellipsis">
            {selectedLabels || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn("w-full p-0 max-h-60 overflow-y-auto", contentClassName)}
        onWheel={(e) => e.stopPropagation()}
      >
        {clearable && (
          <div className="flex w-full">
            {/* Select all only makes sense for a multi select: a single select
                stores one value, not an array. */}
            {isMulti && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={"ghost"}
                    className="flex-1 rounded-none"
                    aria-description={t("common.selectAll")}
                    onClick={() =>
                      onChange?.(filteredOptions.map((opt) => opt.value))
                    }
                    type="button"
                  >
                    <ListChecks className="h-4 w-4 text-primary" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t("common.selectAll")}</TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={"ghost"}
                  className="flex-1 rounded-none"
                  aria-description={t("common.clear")}
                  onClick={() => onChange?.([])}
                  type="button"
                  disabled={selectedLabels.length === 0}
                >
                  <X className="h-4 w-4 text-primary" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("common.clear")}</TooltipContent>
            </Tooltip>
          </div>
        )}

        <Command shouldFilter={false}>
          <CommandInput
            placeholder={placeholder}
            value={search}
            onValueChange={(v) => {
              setSearch(v);
              throttleSearchChange(v);
            }}
          />

          <CommandEmpty>No results found.</CommandEmpty>
          {categories.length === 0 && (
            <CommandGroup>
              {filteredOptions.map((opt, i) => (
                <ComboBoxItem
                  key={String(opt.value) + i.toString()}
                  opt={opt}
                  selected={selected}
                  onSelect={() => toggleValue(opt.value)}
                />
              ))}
            </CommandGroup>
          )}

          {categories.map((cat) => (
            <Fragment key={cat}>
              <CommandGroup heading={cat}>
                {filteredOptions
                  .filter((opt) => opt.category === cat)
                  .map((opt) => (
                    <ComboBoxItem
                      key={String(opt.value)}
                      opt={opt}
                      selected={selected}
                      onSelect={() => toggleValue(opt.value)}
                    />
                  ))}
              </CommandGroup>
            </Fragment>
          ))}
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function ClearableComboboxTrigger({
  disabled,
  selectedLabels,
  placeholder = "Select...",
  clearable = false,
  onChange,
}: {
  disabled?: boolean;
  selectedLabels: string;
  placeholder?: string;
  clearable?: boolean;
  onChange?: (value: any) => void;
}) {
  return (
    <>
      <Button
        variant="outline"
        role="combobox"
        disabled={disabled}
        className="w-full justify-between"
        style={{ zIndex: 100000 }}
      >
        <span className="overflow-hidden text-overflow-ellipsis">
          {selectedLabels || placeholder}
        </span>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>

      {clearable && selectedLabels.length > 0 && (
        <Button
          size="dense"
          variant="ghost"
          className="hover:text-primary"
          onClick={() => onChange?.([])}
          type="button"
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </>
  );
}
